import { Injectable } from '@angular/core';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { WooCommerceProduct } from '../../../interfaces/woo-commerce/api/products/product-api.interface';
import { map, Observable } from 'rxjs';
import { WooCommerceProductCategory } from '../../../interfaces/woo-commerce/api/products/product-category-api.interface';
import { WooCommerceProductParameters } from '../../../interfaces/woo-commerce/products/product.interface';
import { WooCommercePopularProducts } from '../../../interfaces/woo-commerce/api/products/popular-product-api.interface';
import { mapHappyProductToWooProduct } from '../../happy/happy-api-mappers';
import { HappyProductVariant } from '../../../interfaces/happy-api/happy-api.interface';

/**
 * Merges products that share the same name into a single product by combining
 * their variants. This handles cases where the backend incorrectly splits a
 * single product (e.g. "Buffalo Chicken") into separate entries per size.
 */
function mergeProductsByName(products: WooCommerceProduct[]): WooCommerceProduct[] {
  const seen = new Map<string, WooCommerceProduct>();
  const ordered: WooCommerceProduct[] = [];

  for (const product of products) {
    const key = product.name.trim().toLowerCase();
    const existing = seen.get(key);

    if (existing) {
      const merged: HappyProductVariant[] = [
        ...(existing.variants || []),
        ...(product.variants || [])
      ].sort(
        (a, b) =>
          (a.effective_price ?? a.regular_price ?? 0) -
          (b.effective_price ?? b.regular_price ?? 0)
      );
      existing.variants = merged;
      existing.has_variants = merged.length > 0;
      existing.has_available_variants = merged.some((v) => v.is_in_stock);
    } else {
      const clone = { ...product, variants: [...(product.variants || [])] };
      seen.set(key, clone);
      ordered.push(clone);
    }
  }

  return ordered;
}

@Injectable({ providedIn: 'root' })
export class WooCommerceProductApiService extends BaseWooCommerceV2ApiService {
  private readonly happyCatalogUrl: string = '/catalog';

  getProducts(
    params: Partial<WooCommerceProductParameters> = {}
  ): Observable<WooCommerceProduct[]> {
    const happyParams: Record<string, any> = {};
    if (params.per_page) happyParams['per_page'] = params.per_page;
    if (params.page) happyParams['page'] = params.page;
    if (params.category) happyParams['category_id'] = params.category;
    if (params.search) happyParams['search'] = params.search;
    if (params.paginate === false) happyParams['paginate'] = false;

    return this.get<any>(`${this.happyCatalogUrl}/products`, {
      params: happyParams
    }).pipe(
      map((response: any) => {
        const items = response?.data?.data || response?.data || response || [];
        const products = Array.isArray(items) ? items : [];
        return mergeProductsByName(products.map(mapHappyProductToWooProduct));
      })
    );
  }

  getProductsCategories(): Observable<WooCommerceProductCategory[]> {
    return this.get<any>(`${this.happyCatalogUrl}/categories`, {
      params: { per_page: 50 }
    }).pipe(
      map((response: any) => {
        const items = response?.data?.data || response?.data || response || [];
        const categories = Array.isArray(items) ? items : [];
        const parentIds = new Set(
          categories.map((c: any) => c.parent_id).filter(Boolean)
        );
        return categories.map((cat: any) => ({
          ...cat,
          parent: cat.parent_id ?? 0,
          image: cat.thumbnail ?? null,
          count: cat.product_count ?? 0,
          display: cat.display ?? 'default',
          has_children: parentIds.has(cat.id),
          _links: cat._links ?? {}
        }));
      })
    );
  }

  getPopularProducts(): Observable<WooCommercePopularProducts> {
    return this.get<any>(`${this.happyCatalogUrl}/products`, {
      params: { featured: true, per_page: 10 }
    }).pipe(
      map((response: any) => {
        const items = response?.data?.data || response?.data || response || [];
        const products = Array.isArray(items) ? items : [];
        return { products: mergeProductsByName(products.map(mapHappyProductToWooProduct)) } as any;
      })
    );
  }
}
